/**
 * Editor for a choice's destination chain. In V4, every choice has at least
 * one destination entry; the last is the unconditional fallback. The runtime
 * walks the list top-to-bottom and the first matching entry wins.
 *
 * UX modes:
 *   - Bare mode: only the fallback exists. Renders as a single destination
 *     dropdown — the same UI authors had in V1 / V2 before chain editing.
 *   - Chain mode: at least one conditional entry exists. Each entry becomes
 *     a card with "If" header + ConditionBuilder + destination dropdown.
 *     The fallback gets an "Otherwise" header.
 *
 * The "+ Add conditional destination" button always sits below everything.
 *
 * Defensive normalization at render time: if the chain has zero entries or
 * the last entry's condition isn't null, this component fixes that on render
 * (but the writeback to the store only happens when the user actually edits,
 * matching the prototype's "broken imports survive but get fixed on edit"
 * discipline).
 */
import { ChevronDown, ChevronUp, GitBranch, X } from 'lucide-react';
import type { Destination, ScenarioNode, VariableDecl } from '@branchworks/shared';
import { ConditionBuilder } from './ConditionBuilder';

interface DestinationChainEditorProps {
  destinations: Destination[];
  allNodes: Record<string, ScenarioNode>;
  /** The current node's id, so we can exclude it from the destination picker. */
  currentNodeId: string;
  variables: Record<string, VariableDecl>;
  onChange: (next: Destination[]) => void;
}

export function DestinationChainEditor({
  destinations,
  allNodes,
  currentNodeId,
  variables,
  onChange,
}: DestinationChainEditorProps) {
  // Render-time normalization. Same invariant the prototype enforces: at
  // least one entry, last is unconditional.
  const safe = normalize(destinations);
  const lastIdx = safe.length - 1;
  const conditionalEntries = safe.slice(0, lastIdx);
  const fallback = safe[lastIdx];
  const hasConditionals = conditionalEntries.length > 0;
  const hasVariables = Object.keys(variables).length > 0;

  const updateEntry = (idx: number, patch: Partial<Destination>) => {
    onChange(safe.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
  };

  const removeEntry = (idx: number) => {
    onChange(safe.filter((_, i) => i !== idx));
  };

  // The "remove on clear" gesture: when a ConditionBuilder inside a chain
  // entry yields null, remove the entry entirely so it can't shadow the
  // fallback with an always-true (empty-comparisons) condition.
  const updateConditionalCondition = (idx: number, cond: ConditionOrNull) => {
    if (cond === null) {
      removeEntry(idx);
    } else {
      updateEntry(idx, { condition: cond });
    }
  };

  const addConditional = () => {
    if (!hasVariables) return;
    const firstVar = Object.keys(variables)[0];
    const decl = variables[firstVar];
    const newEntry: Destination = {
      condition: {
        combinator: 'all',
        comparisons: [{ variable: firstVar, op: '==', value: decl.default }],
      },
      destinationId: null,
    };
    onChange([...conditionalEntries, newEntry, fallback]);
  };

  /**
   * Move a conditional entry up or down within the conditional portion of the
   * chain. The fallback (last entry) is excluded from reordering — it always
   * stays last by invariant. `direction = -1` is up, `+1` is down.
   */
  const moveEntry = (idx: number, direction: -1 | 1) => {
    const swap = idx + direction;
    if (swap < 0 || swap >= conditionalEntries.length) return;
    const next = conditionalEntries.slice();
    [next[idx], next[swap]] = [next[swap], next[idx]];
    onChange([...next, fallback]);
  };

  const nodeOptions = Object.values(allNodes).filter((n) => n.id !== currentNodeId);

  return (
    <div className="space-y-2">
      {/* Conditional entries (if any) */}
      {conditionalEntries.map((d, i) => (
        <div
          key={i}
          className="rounded border p-2 space-y-1.5 border-cream-border bg-cream-2"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="text-[10px] uppercase tracking-widest text-ink-2">
              Route here if
            </div>
            <div className="flex items-center gap-0.5 flex-shrink-0">
              <button
                onClick={() => moveEntry(i, -1)}
                disabled={i === 0}
                className="p-0.5 rounded hover:bg-cream text-ink-2 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Move up (this entry is checked earlier)"
                aria-label="Move conditional destination up"
              >
                <ChevronUp size={11} />
              </button>
              <button
                onClick={() => moveEntry(i, 1)}
                disabled={i === conditionalEntries.length - 1}
                className="p-0.5 rounded hover:bg-cream text-ink-2 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Move down (this entry is checked later)"
                aria-label="Move conditional destination down"
              >
                <ChevronDown size={11} />
              </button>
              <button
                onClick={() => removeEntry(i)}
                className="p-0.5 rounded hover:bg-cream text-terracotta-dark"
                title="Remove conditional destination"
                aria-label="Remove conditional destination"
              >
                <X size={11} />
              </button>
            </div>
          </div>
          <ConditionBuilder
            condition={d.condition}
            variables={variables}
            onChange={(c) => updateConditionalCondition(i, c)}
            emptyLabel="Empty (will be removed)"
            addLabel="Add comparison"
          />
          <div className="pt-1">{renderDestinationSelect(d.destinationId, nodeOptions, (id) => updateEntry(i, { destinationId: id }))}</div>
        </div>
      ))}

      {/* Fallback. Bare mode: just the select. Chain mode: a card with "Otherwise" header. */}
      {hasConditionals ? (
        <div className="rounded border p-2 space-y-1.5 border-cream-border bg-cream-2">
          <div className="text-[10px] uppercase tracking-widest text-ink-2">Otherwise</div>
          {renderDestinationSelect(fallback.destinationId, nodeOptions, (id) =>
            updateEntry(lastIdx, { destinationId: id }),
          )}
        </div>
      ) : (
        renderDestinationSelect(fallback.destinationId, nodeOptions, (id) =>
          updateEntry(lastIdx, { destinationId: id }),
        )
      )}

      <button
        onClick={addConditional}
        disabled={!hasVariables}
        className="text-xs flex items-center gap-1 hover:underline disabled:opacity-40 disabled:cursor-not-allowed text-ink"
        title={
          hasVariables
            ? 'Add a state-conditional destination. Higher entries are checked first.'
            : 'Declare variables before adding conditional destinations.'
        }
      >
        <GitBranch size={11} /> Add conditional destination
      </button>
    </div>
  );
}

type ConditionOrNull = Parameters<NonNullable<React.ComponentProps<typeof ConditionBuilder>['onChange']>>[0];

function normalize(destinations: Destination[]): Destination[] {
  const list = (destinations || []).slice();
  if (list.length === 0) {
    list.push({ condition: null, destinationId: null });
  }
  const lastIdx = list.length - 1;
  if (list[lastIdx].condition !== null) {
    list[lastIdx] = { ...list[lastIdx], condition: null };
  }
  return list;
}

function renderDestinationSelect(
  current: string | null,
  options: ScenarioNode[],
  onPick: (id: string | null) => void,
) {
  return (
    <select
      value={current || ''}
      onChange={(e) => onPick(e.target.value || null)}
      className="w-full px-2 py-1.5 text-sm rounded border border-cream-border bg-white"
    >
      <option value="">— Choose destination —</option>
      {options.map((n) => (
        <option key={n.id} value={n.id}>
          {n.title || '(untitled)'} ({n.type})
        </option>
      ))}
    </select>
  );
}
